import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { documentosApi } from '../../services/api'
import { compressImage } from '../../lib/comprimirImagen'
import { Modal } from '../ui'
import { confirmar } from '../../lib/confirmar'

// Documentos de una persona: el DNI (frente y dorso, uno vigente por lado) y sus
// recetas. Se usa en el Perfil (pestaña Documentos) y al registrar una asistencia.
//
// Los archivos se piden con la sesión y se muestran como blob: un DNI no tiene
// URL pública, y cada vez que alguien lo abre queda en la auditoría.

const ACEPTA = 'image/jpeg,image/png,image/webp,application/pdf'
// El DNI se comprime menos que una receta: el número y la fecha son chicos.
const comprimirDni = (f) => compressImage(f, 1800, 0.85)

const LADOS = [
  { tipo: 'dni_frente', titulo: 'DNI — Frente', vacio: 'Falta el frente' },
  { tipo: 'dni_dorso', titulo: 'DNI — Dorso', vacio: 'Falta el dorso' },
]

const fechaCorta = (v) => {
  if (!v) return ''
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-AR')
}

const IconoPdf = ({ className = 'h-10 w-10' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M9 13h6M9 17h4" />
  </svg>
)

export default function DocumentosPersona({ personaId, compacto = false }) {
  const [docs, setDocs] = useState([])
  const [cargando, setCargando] = useState(true)
  const [subiendo, setSubiendo] = useState(null) // tipo que se está subiendo
  const [miniaturas, setMiniaturas] = useState({}) // id -> objectURL
  const [visor, setVisor] = useState(null) // { url, mime, titulo }
  const urls = useRef([])

  const guardarUrl = (u) => { urls.current.push(u); return u }
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), [])

  const cargar = useCallback(async () => {
    if (!personaId) return
    try {
      setCargando(true)
      const res = await documentosApi.list(personaId)
      setDocs(res.data?.data || [])
    } catch (err) {
      if (err?.response?.status !== 403) toast.error('No se pudieron cargar los documentos')
    } finally {
      setCargando(false)
    }
  }, [personaId])

  useEffect(() => { cargar() }, [cargar])

  // Miniaturas solo del DNI (son dos): las recetas pueden ser muchas y se abren a pedido.
  useEffect(() => {
    docs
      .filter((d) => d.tipo !== 'receta' && d.mime.startsWith('image/') && !miniaturas[d.id])
      .forEach(async (d) => {
        try {
          const res = await documentosApi.archivo(d.id)
          const url = guardarUrl(URL.createObjectURL(res.data))
          setMiniaturas((m) => ({ ...m, [d.id]: url }))
        } catch { /* la tarjeta queda con el ícono; "Ver" lo reintenta */ }
      })
  }, [docs]) // eslint-disable-line react-hooks/exhaustive-deps

  const abrir = async (d, titulo) => {
    try {
      const url = miniaturas[d.id] || guardarUrl(URL.createObjectURL((await documentosApi.archivo(d.id)).data))
      setVisor({ url, mime: d.mime, titulo })
    } catch (err) {
      toast.error(err?.response?.status === 404 ? 'El archivo ya no está en el servidor' : 'No se pudo abrir el documento')
    }
  }

  const subir = async (tipo, file) => {
    if (!file) return
    try {
      setSubiendo(tipo)
      const listo = tipo === 'receta' ? await compressImage(file) : await comprimirDni(file)
      await documentosApi.upload(personaId, tipo, listo)
      toast.success(tipo === 'receta' ? 'Receta guardada' : 'Foto del DNI guardada')
      await cargar()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo subir el archivo')
    } finally {
      setSubiendo(null)
    }
  }

  const quitar = async (d, que) => {
    if (!(await confirmar({ titulo: `¿Quitar ${que}?`, mensaje: 'El archivo queda guardado en el historial, pero deja de mostrarse.', accion: 'Quitar' }))) return
    try {
      await documentosApi.remove(d.id)
      toast.success('Documento quitado')
      await cargar()
    } catch {
      toast.error('No se pudo quitar el documento')
    }
  }

  const porTipo = (t) => docs.find((d) => d.tipo === t)
  const frente = porTipo('dni_frente')
  const dorso = porTipo('dni_dorso')
  const recetas = docs.filter((d) => d.tipo === 'receta')
  const estado = frente && dorso
    ? { texto: 'DNI completo', clase: 'bg-emerald-100 text-emerald-800' }
    : frente || dorso
      ? { texto: frente ? 'Falta el dorso' : 'Falta el frente', clase: 'bg-amber-100 text-amber-800' }
      : { texto: 'Sin DNI cargado', clase: 'bg-rose-100 text-rose-800' }

  const BotonArchivo = ({ tipo, children, variante = 'primario', camara = false }) => (
    <label className={`cursor-pointer inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-colors ${
      subiendo ? 'pointer-events-none opacity-60' : ''
    } ${variante === 'primario'
      ? 'bg-social-700 text-white hover:bg-social-800'
      : 'bg-white border border-hairline text-ink hover:bg-slate-50'}`}>
      {children}
      <input
        type="file"
        accept={camara ? 'image/*' : ACEPTA}
        {...(camara ? { capture: 'environment' } : {})}
        className="hidden"
        onChange={(e) => { subir(tipo, e.target.files?.[0]); e.target.value = '' }}
      />
    </label>
  )

  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-social-100 text-social-700" aria-hidden="true">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2" /></svg>
          </span>
          <div>
            <h3 className="text-base font-black text-ink">Documentos de la persona</h3>
            <p className="text-xs text-ink-soft">DNI (frente y dorso) y recetas. JPG, PNG o PDF, hasta 10 MB.</p>
          </div>
        </div>
        {!cargando && <span className={`rounded-full px-3 py-1 text-xs font-black ${estado.clase}`}>{estado.texto}</span>}
      </div>

      <div className="card-body space-y-6">
        {/* DNI: un casillero por lado */}
        <div className={`grid gap-4 ${compacto ? 'sm:grid-cols-2' : 'md:grid-cols-2'}`}>
          {LADOS.map((lado) => {
            const d = porTipo(lado.tipo)
            return (
              <div key={lado.tipo} className={`rounded-2xl border-2 ${d ? 'border-emerald-200' : 'border-dashed border-slate-300'} bg-white overflow-hidden`}>
                <div className="flex items-center justify-between px-4 py-2 border-b border-hairline bg-slate-50">
                  <span className="text-xs font-black uppercase tracking-wide text-ink">{lado.titulo}</span>
                  {d
                    ? <span className="text-[11px] font-bold text-emerald-700">✓ Cargado {fechaCorta(d.created_at)}</span>
                    : <span className="text-[11px] font-bold text-rose-700">{lado.vacio}</span>}
                </div>

                {d ? (
                  <>
                    <button type="button" onClick={() => abrir(d, lado.titulo)} className="block w-full bg-slate-100" aria-label={`Ver ${lado.titulo}`}>
                      {d.mime.startsWith('image/') && miniaturas[d.id]
                        ? <img src={miniaturas[d.id]} alt={lado.titulo} className="h-40 w-full object-contain" />
                        : <div className="flex h-40 flex-col items-center justify-center gap-1 text-ink-soft"><IconoPdf /><span className="text-xs font-semibold">{d.mime === 'application/pdf' ? 'PDF — tocá para ver' : 'Cargando…'}</span></div>}
                    </button>
                    <div className="flex flex-wrap gap-2 px-4 py-3">
                      <button type="button" onClick={() => abrir(d, lado.titulo)} className="rounded-xl bg-social-700 px-3 py-2 text-xs font-bold text-white hover:bg-social-800">Ver</button>
                      <BotonArchivo tipo={lado.tipo} variante="secundario">{subiendo === lado.tipo ? 'Subiendo…' : 'Reemplazar'}</BotonArchivo>
                      <button type="button" onClick={() => quitar(d, lado.titulo)} className="ml-auto rounded-xl px-3 py-2 text-xs font-bold text-danger-dark hover:bg-danger-soft">Quitar</button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-3 px-4 py-6 text-center">
                    <p className="text-xs text-ink-soft">Subí una foto nítida o el PDF escaneado.</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <BotonArchivo tipo={lado.tipo}>{subiendo === lado.tipo ? 'Subiendo…' : 'Elegir archivo'}</BotonArchivo>
                      <BotonArchivo tipo={lado.tipo} variante="secundario" camara>📷 Sacar foto</BotonArchivo>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Recetas */}
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-black text-ink">
              Recetas <span className="font-bold text-ink-muted">({recetas.length})</span>
            </h4>
            <div className="flex gap-2">
              <BotonArchivo tipo="receta">{subiendo === 'receta' ? 'Subiendo…' : '+ Subir receta'}</BotonArchivo>
              <BotonArchivo tipo="receta" variante="secundario" camara>📷 Foto</BotonArchivo>
            </div>
          </div>
          {recetas.length === 0 ? (
            <p className="rounded-xl border border-dashed border-hairline px-4 py-3 text-xs text-ink-soft">
              Todavía no hay recetas. Las que se adjunten al registrar una asistencia aparecen acá.
            </p>
          ) : (
            <ul className="divide-y divide-hairline rounded-xl border border-hairline">
              {recetas.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                  <IconoPdf className="h-6 w-6 shrink-0 text-ink-muted" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">{r.nombre_original || 'Receta'}</p>
                    <p className="text-[11px] text-ink-muted">
                      Subida {fechaCorta(r.created_at)}{r.subido_por_usuario ? ` por ${r.subido_por_usuario}` : ''}
                      {r.asistencia_tipo ? ` · Asistencia ${r.asistencia_tipo} del ${fechaCorta(r.asistencia_fecha)}` : ''}
                    </p>
                  </div>
                  <button type="button" onClick={() => abrir(r, r.nombre_original || 'Receta')} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-ink hover:bg-slate-200">Ver</button>
                  <button type="button" onClick={() => quitar(r, 'esta receta')} className="rounded-lg px-2 py-1.5 text-xs font-bold text-danger-dark hover:bg-danger-soft" aria-label="Quitar receta">✕</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Modal isOpen={!!visor} onClose={() => setVisor(null)} title={visor?.titulo} size="xl">
        {visor && (visor.mime === 'application/pdf'
          ? <iframe src={visor.url} title={visor.titulo} className="h-[75vh] w-full rounded-lg border border-hairline" />
          : <img src={visor.url} alt={visor.titulo} className="mx-auto max-h-[75vh] w-auto rounded-lg" />)}
      </Modal>
    </div>
  )
}
